/**
 * bc-events-compat: two-way bridge between Horizon v4's Shopify standard
 * events and the legacy v3 theme events that every bc-*.js script speaks.
 *
 * Horizon v4.0 deleted the v3 event classes (CartAddEvent / CartUpdateEvent /
 * VariantUpdateEvent — the 'cart:update' and 'variant:update' DOM events) and
 * moved to Shopify's hosted standard events ('shopify:product:select',
 * 'shopify:cart:lines-update'), whose payloads arrive by resolving the
 * `event.promise` property instead of riding on `event.detail`.
 *
 * Rather than porting 15 bc scripts (and re-testing every flow), this shim
 * re-materialises the legacy contract:
 *
 *  FORWARD (upstream → bc listeners)
 *   - 'shopify:product:select'    → 'variant:update'  detail.{resource,sourceId,data.{html,productId,newProduct}}
 *   - 'shopify:cart:lines-update' → 'cart:update'     detail.{resource,sourceId,data.{didError,source,productId,itemCount,variantId,sections}}
 *   - 'shopify:cart:discount-update' → 'discount:update' detail.{resource}
 *     (bc-cart-free-shipping listens for the legacy event; without this the
 *     bar goes stale when a discount code is applied/removed on /cart)
 *
 *  REVERSE (bc dispatchers → upstream listeners)
 *   - legacy 'cart:update' whose data.source is a known bc source
 *     → 'shopify:cart:lines-update' with a pre-resolved promise, so the v4
 *     cart items / drawer / header bubble morph from data.sections exactly
 *     as they do for upstream adds.
 *   - legacy 'cart:error' from a known bc source → 'shopify:cart:error', so
 *     failed bc adds (accessories ride-along, kit builder, custom length)
 *     reach v4 listeners (e.g. the sticky bar resets its button state)
 *     instead of dying on a channel nothing subscribes to.
 *
 * Loop safety: every event this shim creates carries __bcEventsCompat and is
 * ignored by the opposite direction. Legacy events dispatched natively by bc
 * scripts reach bc listeners directly (untranslated), so nothing fires twice.
 *
 * If a Horizon update renames the standard events or drops event.promise,
 * this shim goes silent and bc features fail exactly as safely as before —
 * no accidental cart writes, just missing reactivity.
 */
(function () {
  'use strict';

  var FLAG = '__bcEventsCompat';

  /**
   * Sources whose legacy 'cart:update' dispatches must reach the v4 cart UI,
   * mapped to the standard event's `action` (read directly off the event by
   * cart-drawer auto-open and gift-card-recipient-form, not via the promise).
   */
  var BC_CART_SOURCES = {
    'bc-custom-length': 'add',
    'bc-cart-length': 'update',
    'bc-accessories': 'add',
    'bc-primitive-kit-builder': 'add',
    'bc-segment-builder': 'add',
  };

  /** @param {EventTarget|null} target */
  function dispatchTarget(target) {
    return target instanceof Element && target.isConnected ? target : document;
  }

  /* ------------------------------------------------------------------ */
  /* forward: shopify:product:select → variant:update                    */
  /* ------------------------------------------------------------------ */
  document.addEventListener('shopify:product:select', function (event) {
    if (event[FLAG]) return;
    var promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;
    var target = dispatchTarget(event.target);

    promise
      .then(function (result) {
        var d = result && result.detail;
        if (!d || !d.html) return;
        var legacy = new CustomEvent('variant:update', {
          bubbles: true,
          detail: {
            // v4 resolves the parsed variant JSON as detail.resource — the
            // same object v3 passed as the VariantUpdateEvent resource.
            resource: d.resource || result.variant || null,
            sourceId: d.sourceId || '',
            data: {
              html: d.html,
              productId: d.productId || '',
              newProduct: d.newProduct,
            },
          },
        });
        legacy[FLAG] = true;
        dispatchTarget(target).dispatchEvent(legacy);
      })
      .catch(function () {
        /* aborted/failed fetch — v3 dispatched nothing here either */
      });
  });

  /* ------------------------------------------------------------------ */
  /* forward: shopify:cart:lines-update → cart:update                    */
  /* ------------------------------------------------------------------ */
  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (event[FLAG]) return;
    var promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;
    var target = dispatchTarget(event.target);

    promise
      .then(function (result) {
        var d = (result && result.detail) || {};
        var legacy = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: legacyCartFromStandard(result && result.cart),
            sourceId: d.sourceId || '',
            data: {
              didError: d.didError,
              source: d.source,
              productId: d.productId,
              itemCount: d.itemCount,
              variantId: d.variantId,
              sections: d.sections,
            },
          },
        });
        legacy[FLAG] = true;
        dispatchTarget(target).dispatchEvent(legacy);
      })
      .catch(function () {});
  });

  /* ------------------------------------------------------------------ */
  /* forward: shopify:cart:discount-update → discount:update             */
  /* ------------------------------------------------------------------ */
  document.addEventListener('shopify:cart:discount-update', function (event) {
    if (event[FLAG]) return;
    var promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;
    var target = dispatchTarget(event.target);

    promise
      .then(function (result) {
        var legacy = new CustomEvent('discount:update', {
          bubbles: true,
          detail: {
            resource: legacyCartFromStandard(result && result.cart),
          },
        });
        legacy[FLAG] = true;
        dispatchTarget(target).dispatchEvent(legacy);
      })
      .catch(function () {});
  });

  /* ------------------------------------------------------------------ */
  /* reverse: bc 'cart:error' → shopify:cart:error                       */
  /* ------------------------------------------------------------------ */
  document.addEventListener('cart:error', function (event) {
    if (event[FLAG]) return;
    var detail = /** @type {any} */ (event).detail || {};
    var data = detail.data || {};
    // bc dispatchers tag themselves via sourceId (bc-accessories,
    // bc-custom-length) or detail.source (bc-primitive-kit-builder).
    var source = detail.sourceId || detail.source;
    if (!source || String(source).indexOf('bc-') !== 0) return;

    var standard = new Event('shopify:cart:error', { bubbles: true });
    standard[FLAG] = true;
    /** @type {any} */ (standard).error = data.message || 'Add to cart failed';
    /** @type {any} */ (standard).code = 'INVALID';
    /** @type {any} */ (standard).detail = {
      source: source,
      description: data.description,
      errors: data.errors,
    };
    dispatchTarget(event.target).dispatchEvent(standard);
  });

  /**
   * Maps the standard-event cart (totalQuantity / cost.totalAmount) onto the
   * ajax-cart fields legacy listeners read (item_count / total_price, cents).
   * Listeners that need more (bc-cart-free-shipping) already fall back to
   * fetching /cart.js when a field is missing or non-numeric.
   * @param {any} cart
   */
  function legacyCartFromStandard(cart) {
    if (!cart) return null;
    var legacy = {};
    if (typeof cart.totalQuantity === 'number') legacy.item_count = cart.totalQuantity;
    var amount = cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount;
    var cents = Math.round(parseFloat(amount) * 100);
    if (!isNaN(cents)) legacy.total_price = cents;
    return legacy;
  }

  /* ------------------------------------------------------------------ */
  /* reverse: bc 'cart:update' → shopify:cart:lines-update               */
  /* ------------------------------------------------------------------ */
  document.addEventListener('cart:update', function (event) {
    if (event[FLAG]) return;
    var detail = /** @type {any} */ (event).detail || {};
    var data = detail.data || {};
    if (!BC_CART_SOURCES[data.source]) return;

    var standard = new Event('shopify:cart:lines-update', { bubbles: true });
    standard[FLAG] = true;
    /** @type {any} */ (standard).action = BC_CART_SOURCES[data.source];
    /** @type {any} */ (standard).context = 'product';
    /** @type {any} */ (standard).promise = Promise.resolve({
      cart: standardCartFromAjax(detail.resource),
      detail: {
        source: data.source,
        sourceId: detail.sourceId || data.source,
        itemCount: data.itemCount,
        productId: data.productId,
        variantId: data.variantId,
        sections: data.sections,
        items: (detail.resource && detail.resource.items) || undefined,
      },
    });
    dispatchTarget(event.target).dispatchEvent(standard);
  });

  /**
   * Maps an ajax cart (/cart.js, /cart/add.js, /cart/change.js response) onto
   * the minimal standard-cart shape v4 consumers read (header bubble uses
   * cart.totalQuantity; cart items morph from detail.sections instead).
   * @param {any} cart
   */
  function standardCartFromAjax(cart) {
    if (!cart || typeof cart.item_count !== 'number') return null;
    return {
      id: cart.token || '',
      totalQuantity: cart.item_count,
      cost: {
        totalAmount: {
          amount: typeof cart.total_price === 'number' ? (cart.total_price / 100).toFixed(2) : '',
          currencyCode: cart.currency || '',
        },
      },
      lines: [],
    };
  }
})();
